import { useRef, useState, useMemo } from "react";
import GeneralButton from "../common/GeneralButton";
import { HiChevronRight } from "react-icons/hi";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { FaCirclePlus } from "react-icons/fa6";
import { TiChevronLeft, TiChevronRight } from "react-icons/ti";
import { usePlan, useStep } from "../contexts/SubscriptionContext";
import { useNavigate } from "react-router-dom";
import {
  anyPlanHasPriceBadgeText,
  thisPlanHasPriceBadgeText,
} from "../utils/commonHelper";

const PlanSlider = ({ slides }) => {
  const swiperRef = useRef(null);
  const { nextStep, prevStep, goToStep } = useStep();
  const [atBeginning, setAtBeginning] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const { setSelectedPlan } = usePlan();
  const navigate = useNavigate();

  // Ensure Swiper has enough slides for loop
  const slidesForLoop = useMemo(() => {
    return slides;
  }, [slides]);

  return (
    <div className="bg-orange w-full grid grid-cols-1 md:grid-cols-[30%_63%] lg:grid-cols-[25%_69%]  xl:grid-cols-[18%_80%] 1xl:grid-cols-[15%_83%] 2.5xl:grid-cols-[13%_87%] items-start md:items-center px-6 md:px-6 xl:pe-12 2xl:px-12 py-8 gap-8 2.5xl:gap-4">
      {/* Left Text Section */}
      <div className="flex flex-col gap-0">
        <h2 className="text-32pt  leading-none text-white">Getting</h2>
        <h2 className="text-48pt md:text-[30pt] 2xl:text-48pt leading-none font-extrabold text-white mb-2">
          Started
        </h2>
        <GeneralButton
          text="Choose a plan"
          title=""
          bgColor="#30160E"
          textColor="#A0AE3F"
          hoveredTextColor="#A0AE3F"
          className="text-24pt lg:text-20pt 2xl:text-24pt leading-tight cursor-default noHover"
          // onClick={() => {
          //   goToStep("1");
          //   navigate("/plans");
          // }}
          textType=""
          icon={<HiChevronRight className="text-24pt hidden md:block" />}
        />
      </div>

      {/* Right Swiper Section */}
      <div className="relative planSwiperHolder group w-full">
        {/* Custom Navigation Buttons */}
        {!atBeginning && (
          <button
            onClick={() => swiperRef.current?.slidePrev()}
            className="absolute 2.5xl:left-[-25px] lg:left-[-32px] md:left-[-30px] left-[-30px] top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full"
          >
            <TiChevronLeft size={35} className="text-white" />
          </button>
        )}
        {!atEnd && (
          <button
            onClick={() => swiperRef.current?.slideNext()}
            className="hidden md:flex absolute 2.5xl:right-[-40px] lg:right-[-38px] md:right-[-35px] top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full"
          >
            <TiChevronRight size={35} className="text-white" />
          </button>
        )}

        {/* Swiper */}
        <Swiper
          modules={[Navigation]}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            setAtBeginning(swiper.isBeginning);
            setAtEnd(swiper.isEnd);
          }}
          loop={window.innerWidth < 768 ? true : false} // loop always
          onSlideChange={(swiper) => {
            setAtBeginning(swiper.isBeginning);
            setAtEnd(swiper.isEnd);
          }}
          breakpoints={{
            0: { slidesPerView: 2, spaceBetween: 8 },
            640: { slidesPerView: 2, spaceBetween: 8 },
            996: { slidesPerView: 3, spaceBetween: 10 },
            1300: {
              slidesPerView:
                slidesForLoop.length > 3 ? 4 : slidesForLoop.length,
              spaceBetween: 10,
            },
          }}
          spaceBetween={8}
          className="rounded-2xl overflow-hidden "
        >
          {slidesForLoop.map((slide, index) => (
            <SwiperSlide
              key={index}
              className="flex"
              style={{ display: "-ms-flexbox" }}
            >
              <div
                onClick={() => {
                  setSelectedPlan(slide);
                  navigate("/subscribe/plan");
                  goToStep("1");
                }}
                className="bg-white relative flex  flex-[1] items-center justify-start px-10 py-10 cursor-pointer hover:shadow-lg transition-shadow duration-300"
              >
                <div className="min-w-0">
                  <div className="relative">
                    {slide.specialOfferText && (
                      <p
                        style={{ color: slide.specialOfferTextColor }}
                        className={`absolute  left-0 text-14pt md:text-xs font-mono text-secondary capitalize ${
                          !anyPlanHasPriceBadgeText(slidesForLoop)
                            ? "-top-[10px]"
                            : slide?.priceBadgeText != ""
                            ? "-top-[20px]"
                            : "top-[15px]"
                        }`}
                      >
                        {slide.specialOfferText}
                      </p>
                    )}

                    <GeneralButton
                      text={slide.priceBadgeText}
                      className={`2.5xl:text-18pt lg:text-14pt md:text-14pt xxs:text-18pt mb-1 xl:py-1 cursor-default ${
                        anyPlanHasPriceBadgeText(slidesForLoop)
                          ? "min-h-[35px]"
                          : ""
                      }`}
                      bgColor={slide.priceBadgeColor}
                      textColor={slide.badgeTextColor || "#fff"}
                      icon={null}
                    />
                  </div>
                  <h2 className="2.5xl:text-48pt xl:text-40pt lg:text-32pt md:text-28pt xxs:text-32pt leading-none whitespace-nowrap text-secondary capitalize">
                    {slide.title}
                  </h2>
                  <h2 className="xl:text-32pt lg:text-24pt md:text-24pt text-32pt leading-none text-secondary">
                    plan
                  </h2>
                </div>
                <FaCirclePlus
                  title="Click to select plan"
                  className="absolute bottom-3 right-3 xl:text-5xl lg:text-3xl md:text-3xl text-5xl text-secondary hover:text-orange transition-colors duration-200"
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default PlanSlider;
